// Les comptes d'un client — ouverts et gérés par l'éditeur.
//
// C'est le chemin NORMAL de création d'un compte. Un compte se facture, et
// l'éditeur héberge les données : savoir qui accède au logiciel chez ses
// clients fait partie de ce qu'il doit pouvoir répondre. Le libre-service se
// donne au cas par cas (écran « Modules »).
//
// LE MOT DE PASSE NE SE RELIT PAS. Il n'est stocké qu'en haché : on l'affiche
// une fois, en clair, pour qu'il soit transmis — de vive voix de préférence.
// Un champ « voir le mot de passe » n'existera jamais ici.
import { useCallback, useEffect, useState } from "react";

import api from "../api";
import ModaleConfirmation from "./ModaleConfirmation";
import { ROLES, aideRole, labelRole } from "../roles";

const AIDE_MOT_DE_PASSE =
  "12 caractères minimum, sans suite prévisible (1234, abcd), " +
  "sans mot courant, et sans l’adresse e-mail.";

// MOINDRE PRIVILEGE PAR DEFAUT. Le rôle par défaut était « Administrateur »,
// au motif que le premier compte d'un client doit pouvoir gérer les suivants.
// Résultat : un second compte est reparti administrateur sans que personne
// l'ait voulu. L'écran pré-sélectionne maintenant Administrateur UNIQUEMENT
// quand le client n'a encore personne — proposer n'est pas imposer.
const NOUVEAU_VIDE = {
  nom: "",
  prenom: "",
  email: "",
  mot_de_passe: "",
  role: "travailleur",
};


// Une proposition, jamais une imposition : le champ reste modifiable.
//
// Sans elle, on invente un mot de passe à la main, on se fait refuser par la
// politique, et on finit par écrire quelque chose de court dont on se
// souviendra — c'est-à-dire l'inverse du but.
//
// Tirage par `crypto.getRandomValues` et non `Math.random()`, qui n'est pas
// fait pour ça. L'alphabet exclut les caractères qui se confondent à l'oral et
// à la lecture (0/O, 1/l/I) : ce mot de passe se transmet de vive voix.
export function proposerMotDePasse() {
  const alphabet = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  const tirage = new Uint32Array(16);
  crypto.getRandomValues(tirage);
  return Array.from(tirage, (n) => alphabet[n % alphabet.length]).join("");
}

function messageErreur(err, defaut) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return defaut;
}

export default function ComptesClient({ client, onFermer, onChange }) {
  const [comptes, setComptes] = useState(null); // null = pas encore chargé
  const [erreur, setErreur] = useState("");
  const [message, setMessage] = useState("");
  const [ouvert, setOuvert] = useState(false);
  const [nouveau, setNouveau] = useState(NOUVEAU_VIDE);
  const [enCours, setEnCours] = useState(false);

  const charger = useCallback(() => {
    api
      .get(`/plateforme/clients/${client.id}/utilisateurs`)
      .then(({ data }) => setComptes(data))
      .catch((err) => setErreur(messageErreur(err, "Chargement impossible.")));
  }, [client.id]);

  useEffect(charger, [charger]);

  const creer = (e) => {
    e.preventDefault();
    setEnCours(true);
    setErreur("");
    api
      .post(`/plateforme/clients/${client.id}/utilisateurs`, nouveau)
      .then(({ data }) => {
        // Le mot de passe reste affiché dans le formulaire tant qu'on ne l'a
        // pas fermé : c'est la seule occasion de le lire. Le vider tout de
        // suite obligerait à en générer un second.
        setMessage(`${data.message} (${data.email})`);
        setOuvert(false);
        charger();
        onChange?.();
      })
      .catch((err) => setErreur(messageErreur(err, "Création impossible.")))
      .finally(() => setEnCours(false));
  };

  // Compte visé par la fenêtre de suppression, et l'adresse recopiée.
  const [aSupprimer, setASupprimer] = useState(null);
  const [saisieSuppression, setSaisieSuppression] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const [mdpPropose, setMdpPropose] = useState("");

  const basculerActif = (compte) => {
    setErreur("");
    api
      .patch(`/plateforme/clients/${client.id}/utilisateurs/${compte.id}`, {
        actif: !compte.actif,
      })
      .then(() => {
        setMessage(
          compte.actif
            ? `${compte.prenom} ${compte.nom} est désactivé : ses sessions ouvertes sont coupées.`
            : `${compte.prenom} ${compte.nom} peut de nouveau se connecter.`
        );
        charger();
        onChange?.();
      })
      .catch((err) => setErreur(messageErreur(err, "Modification impossible.")));
  };

  // SUPPRIMER, ET NON DÉSACTIVER — l'exception, pas la règle.
  //
  // Ajouté le 18/08 à la demande de Yanis : réparer une erreur de saisie
  // (adresse fautive, compte créé en double) sans laisser une ligne morte
  // dans la liste pour toujours.
  //
  // LA DÉSACTIVATION RESTE LE GESTE NORMAL, et elle est conservée : un salarié
  // qui part se désactive. C'est ce qui garde son nom sur les inspections
  // qu'il a signées. La fenêtre le dit, plutôt que de laisser choisir au
  // hasard entre deux boutons voisins.
  const supprimer = () => {
    const compte = aSupprimer;
    setErreur("");
    setEnCours(true);
    api
      .delete(
        `/plateforme/clients/${client.id}/utilisateurs/${compte.id}`,
        { params: { confirmation: saisieSuppression.trim() } }
      )
      .then(({ data }) => {
        // Le compte rendu vient du SERVEUR, mot pour mot : c'est lui qui sait
        // ce qui a réellement été fait.
        setMessage(data.message);
        setASupprimer(null);
        setSaisieSuppression("");
        charger();
        onChange?.();
      })
      .catch((err) => setErreur(messageErreur(err, "Suppression impossible.")))
      .finally(() => setEnCours(false));
  };

  // RÉINITIALISER, PAS « RETROUVER ». Un mot de passe n'est stocké qu'en
  // haché : perdu, il est perdu. On en pose un nouveau, et on coupe les
  // sessions en cours — c'est justement dans ces cas-là qu'on ignore pourquoi
  // l'ancien a été perdu.
  const reinitialiser = (compte) => {
    // Le mot de passe se saisissait dans une boite `window.prompt` : elle
    // bloque l'onglet, ne se rethème pas, et un champ de mot de passe qui
    // s'affiche en clair dans une fenetre systeme n'est ni copiable proprement
    // ni lisible sur telephone. Meme fenetre que partout ailleurs desormais.
    setMdpPropose(proposerMotDePasse());
    setErreur("");
    setMessage("");
    setConfirmation({
      titre: "Réinitialiser ce mot de passe ?",
      sousTitre: `${compte.prenom} ${compte.nom} — ${compte.email}`,
      message: `${AIDE_MOT_DE_PASSE}\n\nIl s'affiche ici une seule fois : notez-le avant de valider. Les sessions ouvertes de ce compte sont coupées.`,
      champMotDePasse: true,
      libelleConfirmer: "Réinitialiser",
      onConfirmer: async (nouveauMdp) => {
        const { data } = await api.post(
          `/plateforme/clients/${client.id}/utilisateurs/${compte.id}/mot-de-passe`,
          { nouveau_mot_de_passe: nouveauMdp }
        );
        setMessage(`${data.message} (${data.email})`);
      },
    });
  };

  const changerRole = (compte, role) => {
    setErreur("");
    api
      .patch(`/plateforme/clients/${client.id}/utilisateurs/${compte.id}`, { role })
      .then(() => {
        setMessage(
          `${compte.prenom} ${compte.nom} est désormais ${labelRole(role).toLowerCase()} — ` +
            "ses sessions ouvertes sont coupées."
        );
        charger();
      })
      .catch((err) => setErreur(messageErreur(err, "Modification impossible.")));
  };

  return (
    <>
    <div className="modal-overlay" onClick={onFermer}>
      <div
        className="modal-carte modal-large"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="modal-titre">Comptes</h3>
        <p className="modal-sous-titre">
          {client.nom}
          {!client.gestion_comptes_client && (
            <> — ce client ne peut pas ouvrir de compte lui-même.</>
          )}
        </p>

        {message && <p className="note-info">{message}</p>}
        {erreur && <p className="erreur">{erreur}</p>}

        {!ouvert && (
          <div className="modal-actions" style={{ justifyContent: "flex-start" }}>
            <button
              className="btn-accent"
              onClick={() => {
                // Premier compte du client : il lui faut un administrateur,
                // sinon personne ne peut rien gérer chez lui.
                const premier = (comptes || []).length === 0;
                setNouveau({
                  ...NOUVEAU_VIDE,
                  role: premier ? "admin" : "travailleur",
                });
                setOuvert(true);
                setErreur("");
              }}
            >
              + Ouvrir un compte
            </button>
          </div>
        )}

        {ouvert && (
          <form className="formulaire" onSubmit={creer}>
            <div className="grille-form">
              <label className="champ">
                <span className="champ-label">Prénom *</span>
                <input
                  required
                  value={nouveau.prenom}
                  onChange={(e) => setNouveau({ ...nouveau, prenom: e.target.value })}
                />
              </label>
              <label className="champ">
                <span className="champ-label">Nom *</span>
                <input
                  required
                  value={nouveau.nom}
                  onChange={(e) => setNouveau({ ...nouveau, nom: e.target.value })}
                />
              </label>
              <label className="champ">
                <span className="champ-label">E-mail *</span>
                <input
                  required
                  type="email"
                  value={nouveau.email}
                  onChange={(e) => setNouveau({ ...nouveau, email: e.target.value })}
                />
              </label>
              <label className="champ">
                <span className="champ-label">Rôle *</span>
                <select
                  value={nouveau.role}
                  onChange={(e) => setNouveau({ ...nouveau, role: e.target.value })}
                >
                  {ROLES.map((r) => (
                    <option key={r.valeur} value={r.valeur}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </label>
              {/* L'EXPLICATION SUIT LE ROLE CHOISI, quel qu'il soit. Elle ne
                  s'affichait que pour « Administrateur » : choisir
                  « Mécanicien » ne disait alors rien de ce qu'on donne, ni de
                  ce qu'on retire. C'est l'ecran d'ouverture de compte de
                  l'editeur -- le chemin NORMAL -- donc celui ou le choix doit
                  etre eclaire. */}
              <p className="sous-info champ-large">
                {aideRole(nouveau.role)}
                {nouveau.role === "admin" && (
                  <>
                    {" "}Il voit et gère <strong>tous les comptes</strong> de
                    l’entreprise, ainsi que ses réglages d’alertes. Pour
                    quelqu’un qui saisit des interventions, « Travailleur »
                    suffit.
                  </>
                )}
              </p>
              <label className="champ champ-large">
                <span className="champ-label">Mot de passe *</span>
                <div className="champ-avec-bouton">
                  <input
                    required
                    type="text"
                    value={nouveau.mot_de_passe}
                    onChange={(e) =>
                      setNouveau({ ...nouveau, mot_de_passe: e.target.value })
                    }
                  />
                  <button
                    type="button"
                    className="btn"
                    onClick={() =>
                      setNouveau({
                        ...nouveau,
                        mot_de_passe: proposerMotDePasse(),
                      })
                    }
                  >
                    Proposer
                  </button>
                </div>
              </label>
            </div>
            <p className="sous-info">
              {AIDE_MOT_DE_PASSE} Affiché en clair parce que c’est la{" "}
              <strong>seule fois</strong> où il est lisible : il n’est stocké
              que haché. À transmettre de vive voix.
            </p>
            <div className="actions-form">
              <button type="button" className="btn" onClick={() => setOuvert(false)}>
                Annuler
              </button>
              <button className="btn-accent" type="submit" disabled={enCours}>
                {enCours ? "Création…" : "Ouvrir le compte"}
              </button>
            </div>
          </form>
        )}

        {comptes === null ? (
          <p className="note-discrete">Chargement…</p>
        ) : comptes.length === 0 ? (
          <p className="vide">
            Aucun compte. Personne ne peut se connecter chez ce client.
          </p>
        ) : (
          <table className="tableau">
            <thead>
              <tr>
                <th>Nom</th>
                <th>E-mail</th>
                <th>Rôle</th>
                <th>Accès</th>
              </tr>
            </thead>
            <tbody>
              {comptes.map((c) => (
                <tr key={c.id}>
                  <td>
                    {c.prenom} {c.nom}
                  </td>
                  <td>{c.email}</td>
                  <td>
                    <select
                      value={c.role}
                      onChange={(e) => changerRole(c, e.target.value)}
                    >
                      {ROLES.map((r) => (
                        <option key={r.valeur} value={r.valeur}>
                          {r.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="actions-cellule">
                    <button
                      className="lien-action"
                      onClick={() => reinitialiser(c)}
                    >
                      Mot de passe
                    </button>
                    <button
                      className={"lien-action" + (c.actif ? " lien-danger" : "")}
                      onClick={() => basculerActif(c)}
                    >
                      {c.actif ? "Désactiver" : "Réactiver"}
                    </button>
                    {/* La suppression est à côté de la désactivation, pas à sa
                        place : ce sont deux gestes différents, et le plus
                        courant est le second. */}
                    <button
                      className="lien-action lien-danger"
                      onClick={() => {
                        setErreur("");
                        setSaisieSuppression("");
                        setASupprimer(c);
                      }}
                    >
                      Supprimer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="modal-actions">
          <button className="btn" onClick={onFermer}>
            Fermer
          </button>
        </div>

        {/* CONFIRMATION PAR RECOPIE DE L'ADRESSE.
            Pas de « Êtes-vous sûr ? » : une question fermée devient un
            réflexe, et au troisième compte on clique oui sans lire (carnet,
            entrée 20). Le serveur exige déjà l'adresse exacte ; l'écran ne
            fait que refléter cette barrière.

            Et il DIT ce que la suppression coûte, plutôt que de le taire :
            l'historique reste, mais il perd son auteur. C'est le seul moyen
            de choisir en connaissance de cause entre les deux boutons. */}
        {aSupprimer && (
          <div className="modal-overlay" onClick={() => setASupprimer(null)}>
            <div className="modal-carte" onClick={(e) => e.stopPropagation()}>
              <h3 className="modal-titre">Supprimer définitivement ce compte</h3>
              <p className="modal-sous-titre">
                {aSupprimer.prenom} {aSupprimer.nom} — {aSupprimer.email}
              </p>

              <p className="sous-info">
                <strong>La désactivation suffit dans presque tous les cas.</strong>{" "}
                Un salarié qui part se désactive : il ne peut plus se connecter,
                et son nom reste sur les inspections qu'il a signées.
              </p>
              <p className="sous-info">
                La suppression, elle, efface le compte pour de bon. Ce qu'il a
                créé — véhicules, inspections, opérations — <strong>est
                conservé</strong>, mais <strong>sans son nom</strong>. Un rapport
                de VGP gardera son contenu et n'indiquera plus qui l'a rempli.
              </p>

              <label className="champ">
                <span className="champ-label">
                  Pour confirmer, recopiez l’adresse : <code>{aSupprimer.email}</code>
                </span>
                <input
                  value={saisieSuppression}
                  onChange={(e) => setSaisieSuppression(e.target.value)}
                  placeholder={aSupprimer.email}
                  autoFocus
                />
              </label>

              <div className="modal-actions">
                <button className="btn" onClick={() => setASupprimer(null)}>
                  Annuler
                </button>
                <button
                  className="btn-danger"
                  disabled={
                    enCours
                    || saisieSuppression.trim().toLowerCase()
                       !== aSupprimer.email.toLowerCase()
                  }
                  onClick={supprimer}
                >
                  {enCours ? "Suppression…" : "Supprimer définitivement"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    {/* A COTE de la fenetre des comptes, pas dedans : imbriquee, un clic sur
        son voile traverserait jusqu'au voile du dessous et refermerait les
        deux ecrans d'un coup. */}
    {confirmation && (
      <ModaleConfirmation
        {...confirmation}
        onConfirmer={() => confirmation.onConfirmer(mdpPropose)}
        onFermer={() => setConfirmation(null)}
        confirmerDesactive={confirmation.champMotDePasse && !mdpPropose.trim()}
        enfants={
          confirmation.champMotDePasse ? (
            <label className="modal-champ">
              Nouveau mot de passe
              <input
                value={mdpPropose}
                onChange={(e) => setMdpPropose(e.target.value)}
              />
            </label>
          ) : null
        }
      />
    )}
    </>
  );
}
